import { Brand } from "@/api/generated/BMWInterfaces"
import colors from "@/lib/theme/colors"
import styled from "@emotion/styled"
import SliderFilter from "../Homepage/Slider"

import { sendSearchFilter } from "@/lib/gtm"
import {
  Button,
  Checkbox,
  CssBaseline,
  FormControlLabel,
  IconButton,
  SwipeableDrawer,
  Typography,
} from "@mui/material"
import Image from "next/image"
import { SetStateAction } from "react"
import CheckBoxIcon from "../shared/icons/CheckBoxIcon"
import CheckboxCheckedIcon from "../shared/icons/CheckBoxIcon copy"
import { StyledFilterButton } from "./FilterDesktop"
import { VehicleType } from "./Search"

const FilterBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 32px;
  max-height: 100%;
  overflow-y: auto;
  gap: 8px;
  background-color: ${colors.white};
`
const FilterButton = styled(Button)`
  width: 194px;
  height: 50px;
  padding: 11px 20px 11px 20px;
  gap: 10px;
`

const FilterRow = styled.div`
  display: flex;
  width: 100%;
  gap: 16px;
  padding: 8px 24px 8px 16px;
  justify-content: space-between;
  align-items: center;
`
const SelectionBox = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  gap: 8px;

  width: 100%;
`
const TopControls = styled.div`
  display: flex;
  flex-direction: column;
  padding: 8px 16px;
  gap: 4px;
`

const CheckboxesContainer = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 16px 8px;
`

const drawerBleeding = 20

const StyledFormControlLabel = styled(FormControlLabel)<{ checked?: boolean }>`
  margin: 0;
  display: flex;
  gap: 4px;
  align-items: center;
  padding: 6px 8px;
  border: 1px solid ${(props) => (props.checked ? colors.directBlack : colors.directVeryLightGray)};
  border-radius: 6px;
  & .MuiCheckbox-root {
    padding: 0;
    img {
      object-fit: contain;
    }
    svg {
      display: none;
    }
  }
  & .MuiTypography-root {
    font-size: 18px;
    font-weight: 400;
    font-size: 18px;
    line-height: 100%;
    letter-spacing: 0em;
  }
`

interface Props {
  window?: () => Window
  openFilter: boolean
  setOpenFilter: React.Dispatch<SetStateAction<boolean>>
  brands: Brand[]
  setBrandChoose: React.Dispatch<SetStateAction<string>>
  minMaxPrice: number[]
  setMinMaxPrice: React.Dispatch<SetStateAction<number[]>>
  setFiltersCleared: React.Dispatch<SetStateAction<boolean>>
  clearAllFilters: () => Promise<void>
  brandMin: number
  brandMax: number
  handleChosenBrand: (brand: Brand) => () => void
  chosenBrand: string[]
  getNewValuesFilter: () => void
  chosenType: string[]
  types: VehicleType[]
  handleChangeChosenType: (type: VehicleType) => () => void
  engineTypes: { id: number; name: string }[]
  chosenEngineType: string[]
  handleChangeChosenEngineType: (engineType: { id: number; name: string }) => () => void
}

const Root = styled("div")(() => ({
  height: "100%",
  backgroundColor: colors.white,
}))

const Puller = styled("div")(() => ({
  width: 48,
  height: 4,
  backgroundColor: colors.niolightgrey,
  borderRadius: 99,
  alignSelf: "center",
}))

export const SwipeableEdgeDrawer = (props: Props): React.ReactElement => {
  const {
    window,
    setFiltersCleared,
    brands,
    minMaxPrice,
    setMinMaxPrice,
    clearAllFilters,
    brandMin,
    brandMax,
    handleChosenBrand,
    chosenBrand,
    getNewValuesFilter,
    types,
    chosenType,
    handleChangeChosenType,
    engineTypes,
    chosenEngineType,
    handleChangeChosenEngineType,
  } = props

  const filterClick = (): void => {
    sendSearchFilter(chosenBrand.join(","), minMaxPrice.join("-"), chosenType.join(","))
    props.setOpenFilter(false)
    getNewValuesFilter()
  }
  const handleChange = (event: Event, newValue: number | number[]): void => {
    setFiltersCleared(false)
    setMinMaxPrice(newValue as number[])
  }

  const toggleDrawer = (newOpen: boolean) => () => {
    props.setOpenFilter(newOpen)
  }

  const clearFilters = (): void => {
    props.setOpenFilter(false)
    clearAllFilters()
  }

  const hasActiveFilters =
    chosenBrand.length > 0 ||
    chosenType.length > 0 ||
    chosenEngineType.length > 0 ||
    minMaxPrice[0] !== brandMin ||
    minMaxPrice[1] !== brandMax
  const container = window !== undefined ? () => window().document.body : undefined
  return (
    <Root>
      <CssBaseline />

      <SwipeableDrawer
        container={container}
        anchor="bottom"
        open={props.openFilter}
        onClose={toggleDrawer(false)}
        onOpen={toggleDrawer(true)}
        swipeAreaWidth={drawerBleeding}
        disableSwipeToOpen={false}
        ModalProps={{
          keepMounted: true,
        }}
        PaperProps={{
          sx: {
            height: `calc(82% - ${drawerBleeding}px)`,
            borderTopLeftRadius: "50px",
            borderTopRightRadius: "50px",
            overflow: "visible",
          },
        }}
      >
        <TopControls>
          <Puller />
          <IconButton onClick={toggleDrawer(false)} style={{ padding: 4, alignSelf: "end" }}>
            <Image src="/assets/x.svg" alt="close" width={20} height={20} />
          </IconButton>
        </TopControls>
        <FilterBox style={{ paddingBottom: "100px" }}>
          <FilterRow style={{ borderBottom: "1px solid #F2F2F2" }}>
            <Typography variant="h3">סינון רכבים</Typography>
          </FilterRow>
          <FilterRow style={{ borderBottom: "1px solid #F2F2F2" }}>
            <SelectionBox>
              <Typography variant="body1" style={{ padding: "7px 0" }}>
                סוג רכב{" "}
              </Typography>
              <CheckboxesContainer>
                {types.map((type, index) => {
                  const isChecked = chosenType.some((val) => val === type.value?.toString())
                  return (
                    <StyledFormControlLabel
                      key={index}
                      checked={isChecked}
                      control={
                        <Checkbox
                          checked={isChecked}
                          onChange={handleChangeChosenType(type)}
                          icon={<CheckBoxIcon />}
                          checkedIcon={<CheckboxCheckedIcon />}
                        />
                      }
                      label={type.label}
                    />
                  )
                })}
              </CheckboxesContainer>
            </SelectionBox>
          </FilterRow>
          <FilterRow style={{ borderBottom: "1px solid #F2F2F2" }}>
            <SelectionBox>
              <Typography variant="body1" style={{ padding: "7px 0" }}>
                מותג רכב
              </Typography>
              <CheckboxesContainer>
                {brands.map((brand, index) => {
                  const isChecked =
                    chosenBrand.indexOf(brand.id.toString()) > -1 ||
                    (brand.id.toString() === "3" && chosenBrand.indexOf("2") > -1)
                  return (
                    <StyledFormControlLabel
                      key={index}
                      checked={isChecked}
                      control={
                        <Checkbox
                          checked={isChecked}
                          onChange={handleChosenBrand(brand)}
                          icon={
                            brand.logo?.imageUrl && (
                              <Image
                                src={brand.logo?.imageUrl || ""}
                                alt={brand.name || "brand logo"}
                                width={20}
                                height={20}
                              />
                            )
                          }
                          checkedIcon={
                            brand.logo?.imageUrl && (
                              <Image
                                src={brand.logo?.imageUrl || ""}
                                alt={brand.name || "brand logo"}
                                width={20}
                                height={20}
                              />
                            )
                          }
                        />
                      }
                      label={brand.name}
                    />
                  )
                })}
              </CheckboxesContainer>
            </SelectionBox>
          </FilterRow>
          <FilterRow style={{ borderBottom: "1px solid #F2F2F2" }}>
            <SelectionBox>
              <Typography variant="body1" style={{ padding: "7px 0" }}>
                סוג מנוע
              </Typography>
              <CheckboxesContainer>
                {engineTypes.map((engineType, index) => {
                  const isChecked = chosenEngineType.indexOf(engineType.id.toString()) > -1
                  return (
                    <StyledFormControlLabel
                      key={index}
                      checked={isChecked}
                      control={
                        <Checkbox
                          checked={isChecked}
                          onChange={handleChangeChosenEngineType(engineType)}
                          icon={<CheckBoxIcon />}
                          checkedIcon={<CheckboxCheckedIcon />}
                        />
                      }
                      label={engineType.name}
                    />
                  )
                })}
              </CheckboxesContainer>
            </SelectionBox>
          </FilterRow>
          <FilterRow style={{ flexDirection: "column", alignItems: "flex-start" }}>
            <Typography variant="body1">חיפוש לפי תקציב</Typography>
          </FilterRow>
          <FilterRow
            style={{ alignSelf: "center", paddingTop: 40, paddingLeft: 50, paddingRight: 50 }}
          >
            <SliderFilter
              minMaxPrice={minMaxPrice}
              handleChange={handleChange}
              brandMax={brandMax}
              brandMin={brandMin}
            />
          </FilterRow>
          <FilterRow
            style={{
              justifyContent: "space-between",
              gap: 8,
              bottom: 0,
              zIndex: 2,
              padding: 16,
              backgroundColor: colors.white,
              position: "fixed",
            }}
          >
            <FilterButton
              onClick={filterClick}
              variant={hasActiveFilters ? "contained" : "outlined"}
              color="primary"
              sx={{
                color: hasActiveFilters ? colors.white : colors.directBlack,
              }}
            >
              סינון
            </FilterButton>
            <StyledFilterButton onClick={clearFilters} variant="text" disabled={!hasActiveFilters}>
              {hasActiveFilters ? "איפוס" : ""}
            </StyledFilterButton>
          </FilterRow>
        </FilterBox>
      </SwipeableDrawer>
    </Root>
  )
}
